import React from 'react'
import { Modal } from 'react-bootstrap'

// Radiating sunburst mark used in the Frontlands wordmark lockup.
function Sunburst({ size = 20, color = '#1f2937' }) {
    const rays = Array.from({ length: 28 })
    const cx = 50
    const cy = 50
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
            {rays.map((_, i) => {
                const angle = (i / rays.length) * Math.PI * 2
                const inner = 20
                const outer = i % 2 === 0 ? 46 : 38
                return (
                    <line
                        key={i}
                        x1={cx + Math.cos(angle) * inner}
                        y1={cy + Math.sin(angle) * inner}
                        x2={cx + Math.cos(angle) * outer}
                        y2={cy + Math.sin(angle) * outer}
                        stroke={color}
                        strokeWidth={i % 2 === 0 ? 3 : 2}
                        strokeLinecap="round"
                    />
                )
            })}
            <circle cx={cx} cy={cy} r={11} fill={color} />
        </svg>
    )
}

// =============================================================================
// FrontlandsSponsorModal — in-app pop-up for the Frontlands sidebar sponsor ad.
// (DEMO ONLY — no real submission.) Replaces the former standalone new-tab page.
//
// Shows the account's details (company, name, email, zip) prefilled into the
// Frontlands-styled form. Data is passed in directly as props from the Sidebar
// (no new tab, no localStorage handoff). "Go back" closes the modal; "Continue"
// sends the user to the Frontlands website.
// =============================================================================

export default function FrontlandsSponsorModal({ show, onHide, fields = {} }) {
    const handleContinue = () => {
        window.open('https://www.frontlands.com', '_blank', 'noopener,noreferrer')
        onHide()
    }

    return (
        <Modal show={show} onHide={onHide} centered size="lg" contentClassName="fl-modal-content">
            <style>{CSS}</style>
            <div className="fl-modal-body">
                <button type="button" className="fl-modal-close" onClick={onHide} aria-label="Close">
                    &times;
                </button>

                <div className="fl-mheader">
                    <div className="fl-mbrand">
                        <Sunburst size={20} />
                        <span className="fl-mbrand-text">Frontlands</span>
                    </div>
                    <h2 className="fl-mtitle">Turn Your Mineral Rights Into Cash</h2>
                    <p className="fl-msub">It starts here &mdash; just confirm the details below to continue.</p>
                </div>

                <div className="fl-mfield">
                    <label className="fl-mlabel">Company/Individual Name</label>
                    <input className="fl-minput fl-minput--hl" defaultValue={fields.companyName || ''} />
                </div>

                <div className="fl-mfield">
                    <label className="fl-mlabel">First Name</label>
                    <input className="fl-minput" defaultValue={fields.firstName || ''} />
                </div>

                <div className="fl-mfield">
                    <label className="fl-mlabel">Last Name</label>
                    <input className="fl-minput" defaultValue={fields.lastName || ''} />
                </div>

                <div className="fl-mfield">
                    <label className="fl-mlabel">Email Address</label>
                    <input className="fl-minput fl-minput--hl" defaultValue={fields.email || ''} />
                </div>

                <div className="fl-mfield">
                    <label className="fl-mlabel">ZIP Code</label>
                    <input className="fl-minput" defaultValue={fields.zip || ''} placeholder="ZIP Code" />
                </div>

                <div className="fl-mshare">
                    <span className="fl-mcheck">&#10003;</span>
                    By clicking Continue, you&rsquo;ll be redirected to the Frontlands site for more
                    information, and the details above will be shared with them to get you started.
                </div>

                <div className="fl-mactions">
                    <button type="button" className="fl-mbtn fl-mbtn--ghost" onClick={onHide}>
                        Go back
                    </button>
                    <button type="button" className="fl-mbtn fl-mbtn--primary" onClick={handleContinue}>
                        Continue
                    </button>
                </div>
            </div>
        </Modal>
    )
}

const CSS = `
.fl-modal-content {
    border: none;
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0,0,0,.35);
}
.fl-modal-body {
    position: relative;
    padding: 34px 44px 40px;
    background: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.fl-modal-close {
    position: absolute;
    top: 14px;
    right: 16px;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #9ca3af;
    font-size: 26px;
    line-height: 1;
    border-radius: 50%;
    cursor: pointer;
    transition: background 130ms ease, color 130ms ease;
}
.fl-modal-close:hover { background: #f3f4f6; color: #4b5563; }

.fl-mheader {
    text-align: center;
    margin: 2px 0 22px;
}
.fl-mbrand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
}
.fl-mbrand-text {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: .2px;
}
.fl-mtitle {
    font-size: clamp(22px, 3.2vw, 27px);
    font-weight: 800;
    color: #111827;
    line-height: 1.15;
    margin: 0 0 6px;
}
.fl-msub {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.4;
    margin: 0;
}

.fl-mfield { margin-bottom: 7px; }
.fl-mlabel {
    display: block;
    font-size: 12.5px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 3px;
}
.fl-minput {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 12px;
    font-size: 13.5px;
    color: #1f2937;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    outline: none;
    background: #ffffff;
}
.fl-minput--hl { background: #fdfbf3; border-color: #e6dcae; }
.fl-minput:focus { border-color: #cbb76a; box-shadow: 0 0 0 3px rgba(203,181,104,.18); }

.fl-mshare {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 34px 0 34px;
    color: #2e7d32;
    font-size: 13.5px;
    font-weight: 500;
    text-align: center;
}
.fl-mcheck {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #2e7d32;
    color: #fff;
    font-size: 12px;
}

.fl-mactions { display: flex; gap: 16px; justify-content: center; }
.fl-mbtn {
    flex: 1 1 0;
    max-width: 280px;
    padding: 15px 0;
    font-size: 15px;
    font-weight: 700;
    border-radius: 999px;
    cursor: pointer;
    transition: transform .12s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease, filter .15s ease;
}
.fl-mbtn--ghost {
    color: #111827;
    background: #ffffff;
    border: 1px solid #cbb76a;
}
.fl-mbtn--ghost:hover { background: #fcf9ef; border-color: #b9a75c; }
.fl-mbtn--ghost:active { transform: translateY(1px); }

.fl-mbtn--primary {
    color: #2a2205;
    background: linear-gradient(180deg, #d8c67f 0%, #c3ad5c 100%);
    border: 1px solid #b09a4c;
    box-shadow: 0 6px 16px rgba(176,154,76,.35);
    letter-spacing: .01em;
}
.fl-mbtn--primary:hover {
    filter: brightness(1.05);
    box-shadow: 0 10px 24px rgba(176,154,76,.45);
    transform: translateY(-1px);
}
.fl-mbtn--primary:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(176,154,76,.40);
}
`
